import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  type CustomFieldFilterType,
  type FilterType,
  IPTC_FIELDS,
  sortFiltersByOrder,
} from '~/components/FiltersMenu/types';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { usePrivateCustomFieldsContext } from '~/providers/PrivateCustomFieldsProvider';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { type SelectedFilter } from '~/store/filters/types';
import { getSelectCustomFields } from '~/swr-hooks/customFields/utils';
import { useCanViewPrivateAddOn } from '~/swr-hooks/workspaces/addOns/useCanViewPrivateAddOn';

const CUSTOM_FIELDS_COUNT = 3;

export const usePrivateDefaultFastFilterBarOptions = () => {
  const { customFields } = usePrivateCustomFieldsContext();
  const selectCustomFields = useMemo(() => (customFields ? getSelectCustomFields(customFields) : []), [customFields]);
  const libraryId = useURLLibraryIdSelector();
  const boardId = useSelector(centralizedBoardIdSelector);

  const { canViewAddOn: canSeePrivateIPTCFields } = useCanViewPrivateAddOn({ key: 'metadata' });
  const { canViewAddOn: canSeePrivatePeople } = useCanViewPrivateAddOn({ key: 'facialRecognition' });

  const { filters, moreFilters, moreCustomFields, disabledFilters } = useMemo(() => {
    const filters: SelectedFilter[] = [];
    const disabledFilters: FilterType[] = [];
    const customFieldsInAdvanced: CustomFieldFilterType[] = [];

    filters.push({ type: 'locations' });
    filters.push({ type: 'type' });
    filters.push({ type: 'assetFileKind' });

    filters.push({ type: 'tags' });

    filters.push({ type: 'smartTags' });

    if (selectCustomFields.length) {
      /**
       * We don't want to show custom fields on library or board pages.
       */
      if (!libraryId && !boardId) {
        filters.push({ type: 'separator1' });

        selectCustomFields.slice(0, CUSTOM_FIELDS_COUNT).forEach((cf) => {
          filters.push({ type: 'customField', customField: cf });
        });

        if (selectCustomFields.length > CUSTOM_FIELDS_COUNT) {
          customFieldsInAdvanced.push(...selectCustomFields.slice(CUSTOM_FIELDS_COUNT, selectCustomFields.length));
        }
      } else {
        customFieldsInAdvanced.push(...selectCustomFields);
      }
    }

    const advancedFilters: FilterType[] = [
      'separator2',
      'uploader',
      'color',
      'source',
      'extension',
      'orientation',
      'dateCreated',
      'dateModified',
      'dateUploaded',
      'other',
    ];

    if (canSeePrivatePeople) {
      advancedFilters.push('people');
    }

    advancedFilters.push(...IPTC_FIELDS);

    if (!canSeePrivateIPTCFields) {
      disabledFilters.push(...IPTC_FIELDS);
    }

    if (customFieldsInAdvanced.length) {
      advancedFilters.push('customField');
    }

    return {
      filters,
      moreFilters: advancedFilters.sort(sortFiltersByOrder),
      moreCustomFields: customFieldsInAdvanced,
      disabledFilters,
    };
  }, [boardId, canSeePrivateIPTCFields, canSeePrivatePeople, libraryId, selectCustomFields]);

  return {
    filters,
    moreFilters,
    moreCustomFields,
    disabledFilters,
  };
};
